import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import {
  GitHub,
  LinkedIn,
  Email,
  Twitter,
} from '@mui/icons-material';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#080E1B' : '#101B31',
        color: '#F4F7FC',
        py: { xs: 4, md: 5 },
        mt: 'auto',
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? '#26324D' : '#243550'}`,
        '& .footer-link': {
          color: '#C3CDDC',
          width: 'fit-content',
          transition: 'color 160ms ease',
          '&:hover': { color: '#7FE0D5' },
        },
      })}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: 'common.white', letterSpacing: 0.2 }}>
              A3 Research
            </Typography>
            <Typography variant="body2" sx={{ color: '#C3CDDC', mb: 2, lineHeight: 1.75, maxWidth: 440 }}>
              Advancing AI research in healthcare, biotechnology, and intelligent transportation through innovative machine learning approaches and explainable AI systems.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton aria-label="A3 Research on GitHub" size="small" href="https://github.com" target="_blank" rel="noopener" sx={{ color: '#C3CDDC', '&:hover': { color: '#7FE0D5', backgroundColor: 'rgba(127,224,213,0.1)' } }}>
                <GitHub />
              </IconButton>
              <IconButton aria-label="A3 Research on LinkedIn" size="small" href="https://linkedin.com" target="_blank" rel="noopener" sx={{ color: '#C3CDDC', '&:hover': { color: '#7FE0D5', backgroundColor: 'rgba(127,224,213,0.1)' } }}>
                <LinkedIn />
              </IconButton>
              <IconButton aria-label="A3 Research on Twitter" size="small" href="https://twitter.com" target="_blank" rel="noopener" sx={{ color: '#C3CDDC', '&:hover': { color: '#7FE0D5', backgroundColor: 'rgba(127,224,213,0.1)' } }}>
                <Twitter />
              </IconButton>
              <IconButton aria-label="Email A3 Research" size="small" href="mailto:contact@alchemistresearch.com" sx={{ color: '#C3CDDC', '&:hover': { color: '#7FE0D5', backgroundColor: 'rgba(127,224,213,0.1)' } }}>
                <Email />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              Research Areas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link className="footer-link" href="/projects?category=federated-learning" underline="hover">
                Federated Learning
              </Link>
              <Link className="footer-link" href="/projects?category=biomarker-discovery" underline="hover">
                Biomarker Discovery
              </Link>
              <Link className="footer-link" href="/projects?category=automotive-security" underline="hover">
                Automotive Security
              </Link>
              <Link className="footer-link" href="/projects?category=ai-ml" underline="hover">
                AI & Machine Learning
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              Resources
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link className="footer-link" href="/about" underline="hover">
                About
              </Link>
              <Link className="footer-link" href="/projects" underline="hover">
                Projects
              </Link>
              <Link className="footer-link" href="/contact" underline="hover">
                Contact
              </Link>
              <Link className="footer-link" href="/publications" underline="hover">
                Publications
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              Contact Information
            </Typography>
            <Typography variant="body2" sx={{ color: '#C3CDDC', mb: 1, lineHeight: 1.75 }}>
              <strong>Abdulhadi Abbas Akanni</strong><br />
              Research Scientist & AI Engineer<br />
              GITAM University, Bengaluru<br />
              India
            </Typography>
            <Typography variant="body2" sx={{ color: '#C3CDDC' }}>
              Email: aabdulha@gitam.in
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: '#34445D' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#AAB7CA' }}>
            © {currentYear} A3 Research. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link className="footer-link" component={RouterLink} to="/privacy" underline="hover" variant="body2">
              Privacy Policy
            </Link>
            <Link className="footer-link" component={RouterLink} to="/terms" underline="hover" variant="body2">
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
